import { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { ConvexGeometry } from "three/examples/jsm/geometries/ConvexGeometry.js";

import {
  AIRCRAFT_SUPPORT_CASES,
  type ProvenanceMode,
  type SupportCase,
} from "./interpolantProvenanceModel";
import "./InterpolantSupportMap.css";

type SupportMap3DProps = {
  latent: [number, number, number];
  mode: ProvenanceMode;
  pointTone?: SupportMapPointTone;
  rotation?: SupportMapRotation;
  supportCases: SupportCase[];
};

export type SupportMapPointTone = ProvenanceMode | "boundary";

export type SupportMapRotation = {
  x: number;
  y: number;
  z?: number;
};

const SCENE_SCALE = {
  x: 2.25,
  y: 1.65,
  z: 2.05,
};

const DEFAULT_ROTATION: Required<SupportMapRotation> = { x: -0.18, y: 0.42, z: 0 };

const AXIS_LABELS = [
  { id: "x", label: "X", value: "lift/loading" },
  { id: "y", label: "Y", value: "trim/stability" },
  { id: "z", label: "Z", value: "asymmetry/control" },
];

const LATENT_RANGES = [0, 1, 2].map((axisIndex) => {
  const values = AIRCRAFT_SUPPORT_CASES.map((supportCase) => supportCase.latent[axisIndex] ?? 0);
  return {
    min: Math.min(-1.15, ...values),
    max: Math.max(1.15, ...values),
  };
});

function normalize(value: number, axisIndex: number) {
  const range = LATENT_RANGES[axisIndex]!;
  const mid = (range.max + range.min) / 2;
  const half = (range.max - range.min) / 2 || 1;
  return (value - mid) / half;
}

function toSceneVector(latent: [number, number, number]) {
  return new THREE.Vector3(
    normalize(latent[0], 0) * SCENE_SCALE.x,
    normalize(latent[1], 1) * SCENE_SCALE.y,
    normalize(latent[2], 2) * SCENE_SCALE.z,
  );
}

function addAxis(parent: THREE.Object3D, end: THREE.Vector3, color: number) {
  const geometry = new THREE.BufferGeometry().setFromPoints([new THREE.Vector3(0, 0, 0), end]);
  const material = new THREE.LineBasicMaterial({ color, transparent: true, opacity: 0.8 });
  parent.add(new THREE.Line(geometry, material));
}

function disposeObject(object: THREE.Object3D) {
  const maybeMesh = object as THREE.Mesh;
  maybeMesh.geometry?.dispose();
  const material = maybeMesh.material;
  if (Array.isArray(material)) {
    material.forEach((item) => item.dispose());
  } else {
    material?.dispose();
  }
}

function completeRotation(rotation?: SupportMapRotation): Required<SupportMapRotation> {
  return {
    x: rotation?.x ?? DEFAULT_ROTATION.x,
    y: rotation?.y ?? DEFAULT_ROTATION.y,
    z: rotation?.z ?? DEFAULT_ROTATION.z,
  };
}

function colorFromCss(value: string, fallback: string) {
  try {
    return new THREE.Color(value || fallback).getHex();
  } catch {
    return new THREE.Color(fallback).getHex();
  }
}

function numberFromCss(value: string, fallback: number) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function colorForTone(
  tone: SupportMapPointTone,
  palette: { boundary: number; extrapolated: number; interpolated: number },
) {
  if (tone === "boundary") return palette.boundary;
  return tone === "interpolated" ? palette.interpolated : palette.extrapolated;
}

export function InterpolantSupport3D({ latent, mode, pointTone, rotation, supportCases }: SupportMap3DProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const rotationControlledRef = useRef(rotation !== undefined);
  const targetRotationRef = useRef(completeRotation(rotation));
  const targetLatentRef = useRef(latent);
  const targetPointToneRef = useRef<SupportMapPointTone>(pointTone ?? mode);
  const targetSupportCasesRef = useRef(supportCases);
  const [webglUnavailable, setWebglUnavailable] = useState(false);

  useEffect(() => {
    rotationControlledRef.current = rotation !== undefined;
    targetRotationRef.current = completeRotation(rotation);
  }, [rotation]);

  useEffect(() => {
    targetLatentRef.current = latent;
    targetPointToneRef.current = pointTone ?? mode;
    targetSupportCasesRef.current = supportCases;
  }, [latent, mode, pointTone, supportCases]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    if (typeof WebGLRenderingContext === "undefined" && typeof WebGL2RenderingContext === "undefined") {
      setWebglUnavailable(true);
      return;
    }

    let renderer: THREE.WebGLRenderer;
    try {
      renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true, preserveDrawingBuffer: true });
    } catch {
      setWebglUnavailable(true);
      return;
    }

    setWebglUnavailable(false);
    const canvas = renderer.domElement;
    canvas.className = "ipd-3d-canvas";
    canvas.setAttribute("aria-label", "3D reduced aircraft support map");
    container.appendChild(canvas);

    const scene = new THREE.Scene();
    const containerStyle = getComputedStyle(container);
    const sceneBackground = containerStyle.getPropertyValue("--ipd-3d-bg").trim() || "#101314";
    if (sceneBackground.toLowerCase() !== "transparent") {
      scene.background = new THREE.Color(sceneBackground);
    }
    const palette = {
      axisX: colorFromCss(containerStyle.getPropertyValue("--ipd-3d-axis-x").trim(), "#7dd3fc"),
      axisY: colorFromCss(containerStyle.getPropertyValue("--ipd-3d-axis-y").trim(), "#86efac"),
      axisZ: colorFromCss(containerStyle.getPropertyValue("--ipd-3d-axis-z").trim(), "#fbbf24"),
      boundary: colorFromCss(containerStyle.getPropertyValue("--ipd-3d-boundary-color").trim(), "#b69aff"),
      extrapolated: colorFromCss(containerStyle.getPropertyValue("--ipd-3d-extrapolated-color").trim(), "#fbbf24"),
      gridPrimary: colorFromCss(containerStyle.getPropertyValue("--ipd-3d-grid-primary").trim(), "#31434a"),
      gridSecondary: colorFromCss(containerStyle.getPropertyValue("--ipd-3d-grid-secondary").trim(), "#263136"),
      hull: colorFromCss(containerStyle.getPropertyValue("--ipd-3d-hull-color").trim(), "#7dd3fc"),
      hullEdge: colorFromCss(containerStyle.getPropertyValue("--ipd-3d-hull-edge-color").trim(), "#7dd3fc"),
      interpolated: colorFromCss(containerStyle.getPropertyValue("--ipd-3d-interpolated-color").trim(), "#86efac"),
      link: colorFromCss(containerStyle.getPropertyValue("--ipd-3d-link-color").trim(), "#fbbf24"),
      sample: colorFromCss(containerStyle.getPropertyValue("--ipd-3d-sample-color").trim(), "#d8d0c2"),
      support: colorFromCss(containerStyle.getPropertyValue("--ipd-3d-support-color").trim(), "#fbbf24"),
      supportHalo: colorFromCss(containerStyle.getPropertyValue("--ipd-3d-support-halo-color").trim(), "#fbbf24"),
    };
    const opacity = {
      hull: numberFromCss(containerStyle.getPropertyValue("--ipd-3d-hull-opacity").trim(), 0.11),
      hullEdge: numberFromCss(containerStyle.getPropertyValue("--ipd-3d-hull-edge-opacity").trim(), 0.46),
      link: numberFromCss(containerStyle.getPropertyValue("--ipd-3d-link-opacity").trim(), 0.44),
      sample: numberFromCss(containerStyle.getPropertyValue("--ipd-3d-sample-opacity").trim(), 0.36),
      supportHalo: numberFromCss(containerStyle.getPropertyValue("--ipd-3d-support-halo-opacity").trim(), 0.23),
    };

    const camera = new THREE.PerspectiveCamera(42, 1, 0.1, 100);
    camera.position.set(4.9, 3.4, 5.2);
    camera.lookAt(0, 0, 0);

    const root = new THREE.Group();
    const initialRotation = targetRotationRef.current;
    root.rotation.set(initialRotation.x, initialRotation.y, initialRotation.z);
    scene.add(root);

    const grid = new THREE.GridHelper(5.4, 9, palette.gridPrimary, palette.gridSecondary);
    grid.position.y = -1.72;
    root.add(grid);

    addAxis(root, new THREE.Vector3(SCENE_SCALE.x + 0.45, 0, 0), palette.axisX);
    addAxis(root, new THREE.Vector3(0, SCENE_SCALE.y + 0.45, 0), palette.axisY);
    addAxis(root, new THREE.Vector3(0, 0, SCENE_SCALE.z + 0.45), palette.axisZ);

    const hullPoints = AIRCRAFT_SUPPORT_CASES.map((supportCase) => toSceneVector(supportCase.latent));
    const hullGeometry = new ConvexGeometry(hullPoints);
    const hullMaterial = new THREE.MeshBasicMaterial({
      color: palette.hull,
      depthWrite: false,
      opacity: opacity.hull,
      side: THREE.DoubleSide,
      transparent: true,
    });
    const hull = new THREE.Mesh(hullGeometry, hullMaterial);
    root.add(hull);

    const hullEdges = new THREE.LineSegments(
      new THREE.EdgesGeometry(hullGeometry, 12),
      new THREE.LineBasicMaterial({ color: palette.hullEdge, transparent: true, opacity: opacity.hullEdge }),
    );
    root.add(hullEdges);

    const sampleGeometry = new THREE.SphereGeometry(0.045, 12, 8);
    const sampleMaterial = new THREE.MeshBasicMaterial({ color: palette.sample, transparent: true, opacity: opacity.sample });
    const supportGeometry = new THREE.SphereGeometry(0.085, 18, 12);
    const supportMaterial = new THREE.MeshBasicMaterial({ color: palette.support });
    const supportHaloGeometry = new THREE.SphereGeometry(0.19, 16, 10);
    const supportHaloMaterial = new THREE.MeshBasicMaterial({
      color: palette.supportHalo,
      opacity: opacity.supportHalo,
      transparent: true,
      wireframe: true,
    });
    const supportMeshes = new Map<string, { halo: THREE.Mesh; support: THREE.Mesh }>();

    for (const supportCase of AIRCRAFT_SUPPORT_CASES) {
      const point = toSceneVector(supportCase.latent);
      const sample = new THREE.Mesh(sampleGeometry, sampleMaterial);
      sample.position.copy(point);
      root.add(sample);

      const support = new THREE.Mesh(supportGeometry, supportMaterial);
      support.position.copy(point);
      support.visible = false;
      root.add(support);

      const halo = new THREE.Mesh(supportHaloGeometry, supportHaloMaterial);
      halo.position.copy(point);
      halo.scale.setScalar(0.01);
      halo.visible = false;
      root.add(halo);
      supportMeshes.set(supportCase.id, { halo, support });
    }

    const queryColor = colorForTone(pointTone ?? mode, palette);
    const queryPosition = toSceneVector(targetLatentRef.current);
    const queryMaterial = new THREE.MeshBasicMaterial({ color: queryColor });
    const query = new THREE.Mesh(
      new THREE.SphereGeometry(0.14, 24, 16),
      queryMaterial,
    );
    query.position.copy(queryPosition);
    root.add(query);

    const queryHaloMaterial = new THREE.MeshBasicMaterial({ color: queryColor, opacity: 0.34, transparent: true, wireframe: true });
    const queryHalo = new THREE.Mesh(
      new THREE.SphereGeometry(0.34, 24, 16),
      queryHaloMaterial,
    );
    queryHalo.position.copy(queryPosition);
    root.add(queryHalo);

    const linkMaterial = new THREE.LineBasicMaterial({ color: palette.link, transparent: true, opacity: opacity.link });
    const linkLines = Array.from({ length: 6 }, () => {
      const linkGeometry = new THREE.BufferGeometry().setFromPoints([queryPosition, queryPosition]);
      const line = new THREE.Line(linkGeometry, linkMaterial);
      line.visible = false;
      root.add(line);
      return line;
    });

    const light = new THREE.DirectionalLight(0xffffff, 0.9);
    light.position.set(2, 4, 3);
    scene.add(light);

    let frame = 0;
    let pointerStart: { x: number; y: number; rootX: number; rootY: number } | null = null;

    const resize = () => {
      const width = Math.max(280, container.clientWidth);
      const height = Math.max(320, container.clientHeight);
      renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
      renderer.setSize(width, height, false);
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
    };

    const render = () => {
      const targetQueryPosition = toSceneVector(targetLatentRef.current);
      query.position.lerp(targetQueryPosition, 0.09);
      queryHalo.position.copy(query.position);

      const nextColor = colorForTone(targetPointToneRef.current, palette);
      queryMaterial.color.lerp(new THREE.Color(nextColor), 0.16);
      queryHaloMaterial.color.copy(queryMaterial.color);

      const activeSupports = targetSupportCasesRef.current;
      const activeSupportIds = new Set(activeSupports.map((support) => support.id));
      const activeWeights = new Map(activeSupports.map((support) => [support.id, support.weight]));
      for (const supportCase of AIRCRAFT_SUPPORT_CASES) {
        const meshes = supportMeshes.get(supportCase.id);
        if (!meshes) continue;
        const active = activeSupportIds.has(supportCase.id);
        meshes.support.visible = active;
        meshes.halo.visible = active;
        if (active) {
          const weight = activeWeights.get(supportCase.id) ?? 0;
          const targetScale = 0.72 + weight * 2.8;
          meshes.halo.scale.lerp(new THREE.Vector3(targetScale, targetScale, targetScale), 0.14);
        } else {
          meshes.halo.scale.lerp(new THREE.Vector3(0.01, 0.01, 0.01), 0.18);
        }
      }

      linkLines.forEach((line, index) => {
        const support = activeSupports[index];
        const supportCase = support ? AIRCRAFT_SUPPORT_CASES.find((candidate) => candidate.id === support.id) : null;
        if (!supportCase) {
          line.visible = false;
          return;
        }
        line.visible = true;
        const linkGeometry = line.geometry as THREE.BufferGeometry;
        linkGeometry.setFromPoints([query.position, toSceneVector(supportCase.latent)]);
      });

      if (rotationControlledRef.current) {
        const targetRotation = targetRotationRef.current;
        root.rotation.x += (targetRotation.x - root.rotation.x) * 0.08;
        root.rotation.y += (targetRotation.y - root.rotation.y) * 0.08;
        root.rotation.z += (targetRotation.z - root.rotation.z) * 0.08;
      }
      renderer.render(scene, camera);
      frame = window.requestAnimationFrame(render);
    };

    const onPointerDown = (event: PointerEvent) => {
      pointerStart = { x: event.clientX, y: event.clientY, rootX: root.rotation.x, rootY: root.rotation.y };
      canvas.setPointerCapture(event.pointerId);
    };
    const onPointerMove = (event: PointerEvent) => {
      if (!pointerStart) return;
      root.rotation.y = pointerStart.rootY + (event.clientX - pointerStart.x) * 0.008;
      root.rotation.x = pointerStart.rootX + (event.clientY - pointerStart.y) * 0.006;
    };
    const onPointerUp = (event: PointerEvent) => {
      pointerStart = null;
      canvas.releasePointerCapture(event.pointerId);
    };

    const observer = new ResizeObserver(resize);
    observer.observe(container);
    canvas.addEventListener("pointerdown", onPointerDown);
    canvas.addEventListener("pointermove", onPointerMove);
    canvas.addEventListener("pointerup", onPointerUp);
    canvas.addEventListener("pointercancel", onPointerUp);

    resize();
    render();

    return () => {
      window.cancelAnimationFrame(frame);
      observer.disconnect();
      canvas.removeEventListener("pointerdown", onPointerDown);
      canvas.removeEventListener("pointermove", onPointerMove);
      canvas.removeEventListener("pointerup", onPointerUp);
      canvas.removeEventListener("pointercancel", onPointerUp);
      scene.traverse(disposeObject);
      renderer.dispose();
      canvas.remove();
    };
  }, []);

  return (
    <div className="ipd-3d-stage" data-testid="support-map-3d">
      <div ref={containerRef} className="ipd-3d-canvas-host" />
      {webglUnavailable ? <div className="ipd-3d-fallback">3D support map unavailable in this browser.</div> : null}
      <div className="ipd-3d-hull-label">Convex CFD support hull</div>
      <div className="ipd-3d-axis-legend" aria-label="3D reduced aircraft coordinates">
        {AXIS_LABELS.map((axis) => (
          <div className={`ipd-3d-axis-pill ${axis.id}`} key={axis.id}>
            <span>{axis.label}</span>
            <strong>{axis.value}</strong>
          </div>
        ))}
      </div>
    </div>
  );
}
